import { DateFormatter } from '@repo/toolkit/date'
import Checkbox from '@repo/ui/checkbox'
import DateStringInput from '@repo/ui/date-string-input'
import { EmailIcon, UserTwoIcon } from '@repo/ui/icons'
import Input from '@repo/ui/input'
import Typography from '@repo/ui/typography'
import { useFormContext } from 'react-hook-form'

import { useScrollTo } from '../../../../lib/hooks/use-scroll-to'
import useTranslate from '../../../../lib/hooks/use-translate'
import { contactViaMapping } from '../../../../lib/types/enums/score-cards/contact-via.enum'
import { ScoreCardInput } from '../../../../lib/types/models/score-card/score-card-input'
import { StaffApplicationLight } from '../../../../lib/types/models/staff-application/staff-application'
import { getSelectedEnumOption } from '../../../../lib/utils/functions/misc.functions'
import { FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledDateStringInput from '../../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledPhoneInput from '../../../elements/inputs/controlled-phone-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'

interface CandidateDetailsProps {
  staffApplication: StaffApplicationLight
  disabled: boolean
}

export const PcrScoreCardFormCandidateDetails = ({
  staffApplication,
  disabled
}: CandidateDetailsProps) => {
  const { currentLanguage } = useTranslate()
  const form = useFormContext<ScoreCardInput>()

  return (
    <div className={'flex flex-col gap-3'}>
      <Typography variant={'label-semibold'}>Détails Personnels</Typography>
      <div className={'flex flex-col gap-2'}>
        <Input
          icon={UserTwoIcon}
          type={'text'}
          label={'Prénom'}
          disabled={disabled}
          value={staffApplication.firstName}
        />

        <Input
          icon={UserTwoIcon}
          type={'text'}
          label={'NOM'}
          disabled={disabled}
          value={staffApplication.lastName}
        />

        <FormField
          control={form.control}
          name={'email'}
          render={({ field }) => (
            <FormItem className={'flex-1'}>
              <FormControl>
                <ControlledInput {...field} icon={EmailIcon} type={'email'} label={'E-mail'} />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'phoneNumber'}
          render={({ field }) => (
            <FormItem className={'flex-1'}>
              <FormControl>
                <ControlledPhoneInput {...field} label={'Téléphone'} defaultCountry="FR" />
              </FormControl>
            </FormItem>
          )}
        />

        <DateStringInput
          language={currentLanguage}
          value={DateFormatter.toDateFormatted(staffApplication.createdAt)}
          label={'Date de la candidature'}
          disabled={disabled}
          iconIsRight={false}
        />
      </div>
    </div>
  )
}

export const DateAndMethodOfContact = () => {
  const newSearchParams = new URLSearchParams(location.search)
  const form = useFormContext<ScoreCardInput>()
  const contactMethodOptions = Object.entries(contactViaMapping).map(([key, value]) => ({
    label: value,
    value: key
  }))

  const {
    firstContactVia,
    secondContactVia,
    firstContactDate,
    secondContactDate,
    thirdContactDate,
    classifiedDate
  } = form.watch()

  useScrollTo(newSearchParams.get('scroll-to'), { removeParamFromUrl: true })

  return (
    <div className={'flex flex-col gap-3'}>
      <Typography variant={'label-semibold'}>Date et moyen du contact</Typography>

      <div className={'flex flex-col gap-2'} id={'first-contact-section'}>
        <div className={'text-primary flex flex-row items-center gap-2'}>
          <Typography
            variant={'label-semibold'}
            className={'w-5 h-5 flex justify-center items-center bg-primary-light rounded-xs'}
          >
            1
          </Typography>

          <Typography variant={'label-semibold'}>Premier contact établi</Typography>
        </div>

        <div className={'flex gap-2 flex-row'}>
          <FormField
            control={form.control}
            name={'firstContactVia'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    onChange={(value) => {
                      if (!firstContactDate)
                        form.setValue(
                          'firstContactDate',
                          DateFormatter.toDateFormatted(new Date()),
                          { shouldValidate: true }
                        )
                      field.onChange(value)
                    }}
                    options={contactMethodOptions}
                    selectedOption={getSelectedEnumOption(field.value, contactViaMapping)}
                    className="h-8"
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'firstContactDate'}
            render={({ field }) => (
              <FormItem className={'flex-1 h-full'}>
                <FormControl>
                  <ControlledDateStringInput {...field} type={'appointment'} mode="over" />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>

      <div className={'flex flex-col gap-2'} id={'second-contact-section'}>
        <div className={'text-primary flex flex-row items-center gap-2'}>
          <Typography
            variant={'label-semibold'}
            className={'w-5 h-5 flex justify-center items-center bg-primary-light rounded-xs'}
          >
            2
          </Typography>

          <Typography variant={'label-semibold'}>Second contact établi </Typography>
        </div>

        <div className={'flex gap-2 flex-row'}>
          <FormField
            control={form.control}
            name={'secondContactVia'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    disabled={!firstContactVia}
                    options={contactMethodOptions}
                    onChange={(value) => {
                      if (!secondContactDate)
                        form.setValue(
                          'secondContactDate',
                          DateFormatter.toDateFormatted(new Date()),
                          { shouldValidate: true }
                        )
                      field.onChange(value)
                    }}
                    selectedOption={getSelectedEnumOption(field.value, contactViaMapping)}
                    className="h-8"
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'secondContactDate'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledDateStringInput
                    {...field}
                    type={'appointment'}
                    disabled={!firstContactVia}
                    mode="over"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>

      <div className={'flex flex-col gap-2'} id={'third-contact-section'}>
        <div className={'text-primary flex flex-row items-center gap-2'}>
          <Typography
            variant={'label-semibold'}
            className={'w-5 h-5 flex justify-center items-center bg-primary-light rounded-xs'}
          >
            3
          </Typography>

          <Typography variant={'label-semibold'}>Troisième contact établi </Typography>
        </div>

        <div className={'flex gap-2 flex-row'}>
          <FormField
            control={form.control}
            name={'thirdContactVia'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    onChange={(value) => {
                      if (!thirdContactDate)
                        form.setValue(
                          'thirdContactDate',
                          DateFormatter.toDateFormatted(new Date()),
                          { shouldValidate: true }
                        )
                      field.onChange(value)
                    }}
                    disabled={!secondContactVia}
                    options={contactMethodOptions}
                    selectedOption={getSelectedEnumOption(field.value, contactViaMapping)}
                    className="h-8"
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'thirdContactDate'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledDateStringInput
                    {...field}
                    type={'appointment'}
                    disabled={!secondContactVia}
                    mode="over"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>

      <div className={'flex gap-3 flex-row items-center border-t border-grey-stroke pt-3'}>
        <FormField
          control={form.control}
          name={'classifiedDate'}
          render={({ field }) => (
            <FormItem className={'flex items-center gap-3'}>
              <FormControl>
                <Checkbox
                  checked={!!field.value}
                  onChange={(checked) => {
                    field.onChange(checked ? DateFormatter.toDateFormatted(new Date()) : null)
                  }}
                  component={
                    <Typography variant={'label-semibold'} color={'currentColor'}>
                      Classé sans suite
                    </Typography>
                  }
                  wrapperClassName={'text-primary'}
                  labelClassName={'text-primary'}
                />
              </FormControl>
            </FormItem>
          )}
        />

        {classifiedDate && (
          <FormField
            control={form.control}
            name={'classifiedDate'}
            render={({ field }) => (
              <FormItem className={'flex-1 max-w-80'}>
                <FormControl>
                  <ControlledDateStringInput {...field} type={'appointment'} mode="over" />
                </FormControl>
              </FormItem>
            )}
          />
        )}
      </div>
    </div>
  )
}
